import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Edit3,
  ExternalLink,
  Sparkles,
  Loader2,
  Send
} from 'lucide-react';
import { useStorefrontData } from '../../context/StorefrontDataContext';
import LivePageDrawer, { SectionDescriptor, DEFAULT_HOMEPAGE_SECTIONS } from './LivePageDrawer';
import UnifiedLiveTopBar from './UnifiedLiveTopBar';

interface LiveStorefrontBarProps {
  pageName?: string;
  sections?: SectionDescriptor[];
}

export default function LiveStorefrontBar({
  pageName = 'Homepage',
  sections = DEFAULT_HOMEPAGE_SECTIONS
}: LiveStorefrontBarProps) {
  const {
    isAdmin,
    isEditMode,
    isPreviewMode,
    setIsEditMode,
    setIsPreviewMode,
    saveStatus,
    lastSavedTime,
    hasUnpublishedChanges,
    saveDraftManual,
    publishToLive,
    viewSource,
    setViewSource
  } = useStorefrontData();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // If user is not admin, do not render anything
  if (!isAdmin) return null;

  const handleConfirmPublish = async () => {
    setIsPublishing(true);
    await publishToLive();
    setIsPublishing(false);
    setShowPublishModal(false);
  };

  return (
    <>
      <UnifiedLiveTopBar
        pageTitle={pageName}
        isAdmin={isAdmin}
        isEditMode={isEditMode}
        isPreviewMode={isPreviewMode}
        viewSource={viewSource}
        onToggleViewSource={setViewSource}
        onToggleMode={(mode) => {
          if (mode === 'edit') {
            setIsEditMode(true);
            setIsPreviewMode(false);
          } else {
            setIsPreviewMode(true);
            setIsEditMode(false);
          }
        }}
        saveStatus={saveStatus}
        lastSavedTime={lastSavedTime}
        hasUnpublishedChanges={hasUnpublishedChanges}
        onManualSave={saveDraftManual}
        onOpenDrawer={() => setIsDrawerOpen(true)}
        onPublish={() => setShowPublishModal(true)}
        publishLabel="Publish ke Publik"
        onClose={() => {
          setIsEditMode(false);
          setIsPreviewMode(false);
          setViewSource?.('published');
        }}
        adminReturnUrl="/admin/cms"
      />

      {/* Slide-out Settings Drawer */}
      <LivePageDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        pageTitle={pageName}
        sections={sections}
      />

      {/* Confirmation Modal to Publish to Public */}
      {showPublishModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          data-lenis-prevent="true"
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-border-subtle text-ink">
            <div className="flex items-center gap-3 pb-3 border-b border-border-subtle">
              <div className="w-10 h-10 rounded-xl bg-brand-wine/10 flex items-center justify-center text-brand-wine shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-serif font-bold text-ink">Publish ke Pengunjung?</h3>
                <p className="text-xs text-muted">Perubahan draf akan segera tampil secara langsung ke publik.</p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200/80 text-amber-900 text-xs leading-relaxed">
              Setelah dipublikasikan, seluruh pengunjung website akan langsung melihat teks, foto, dan banner terbaru yang telah Anda ubah.
            </div>

            <div className="mt-6 flex items-center justify-end gap-2.5">
              <button
                type="button"
                disabled={isPublishing}
                onClick={() => setShowPublishModal(false)}
                className="px-4 py-2 text-xs font-semibold text-muted hover:text-ink hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isPublishing}
                onClick={handleConfirmPublish}
                className="flex items-center gap-2 px-4 py-2 bg-brand-wine hover:bg-brand-wine/90 text-white text-xs font-bold rounded-lg shadow transition-colors cursor-pointer disabled:opacity-50"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Mempublikasikan...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Ya, Publish Sekarang</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
